import { FC, ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { SKUPageType } from "@/data/types";
import { ChooseBuilderDialog } from "@/dialogs/choose-builder-dialog";
import { useFeatureFlag } from "@/hooks/use-feature-flags";
import { ACCESS_LEGITIMATE_CMS } from "@/lib/features";

type Props = {
  pageType: SKUPageType;
  skuId: string;
  label: string;
  description: string;
  icon: ReactNode;
};

// TODO: Remove after releasing feature flags from WorkOS
const CMS_FLAG = "cms_alpha";

export const WebpageBuilderSelector: FC<Props> = ({
  pageType,
  skuId,
  label,
  description,
  icon,
}) => {
  const [searchParams] = useSearchParams();
  const shouldAccessCMS = useFeatureFlag(ACCESS_LEGITIMATE_CMS);

  const hasFlag =
    shouldAccessCMS ||
    searchParams.get(CMS_FLAG) ||
    localStorage.getItem(CMS_FLAG);

  const shouldAllowEdit = hasFlag && pageType === "legitimate";

  return (
    <Card>
      <CardHeader>
        <h4 className="text-lg font-medium leading-none">Webpage Builder</h4>
        <p className="text-sm text-muted-foreground">
          Select how you would like to build your digital experience.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col xl:flex-row justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="text-sm">{icon}</div>
              <p className="text-sm font-medium">{label}</p>
            </div>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            <ChooseBuilderDialog skuId={skuId} pageType={pageType} />
            {shouldAllowEdit && (
              <Button asChild>
                <Link to={`/skus/${skuId}/web-experience`}>Edit webpage</Link>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
